/**
 * ExecutionTrendChart - Chart showing execution trends over time
 *
 * Displays successful vs failed executions over the selected time period.
 */
import { useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import type { ExecutionTrendData } from '@/hooks/useConnectorStats';

interface ExecutionTrendChartProps {
  data: ExecutionTrendData[];
  periodDays: number;
  isLoading: boolean;
}

/**
 * Resolve a CSS custom property to its computed value, with a safe fallback
 * for SSR and jsdom environments where getComputedStyle is unavailable.
 */
function tokenColor(varName: string, fallback: string): string {
  if (typeof window === 'undefined' || !window.getComputedStyle) return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
  return v || fallback;
}

/**
 * Format date for chart axis
 * Parses date-only strings as UTC midnight, matching the backend's UTC bucket dates.
 */
export function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/**
 * Custom tooltip for the chart
 */
interface TooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
}

function CustomTooltip({ active, payload, label }: TooltipProps) {
  if (!active || !payload) return null;

  const successful = payload[0]?.value || 0;
  const failed = payload[1]?.value || 0;
  const total = successful + failed;
  const successRate = total > 0 ? ((successful / total) * 100).toFixed(1) : '0.0';

  return (
    <div className="bg-popover border border-border rounded-lg shadow-lg p-3">
      <p className="text-sm font-medium mb-2">{formatDate(label || '')}</p>
      <div className="space-y-1">
        <p className="text-sm text-success">
          Successful: <span className="font-medium">{successful}</span>
        </p>
        <p className="text-sm text-destructive">
          Failed: <span className="font-medium">{failed}</span>
        </p>
        <p className="text-sm text-muted-foreground border-t pt-1 mt-1">
          Total: <span className="font-medium">{total}</span> ({successRate}% success)
        </p>
      </div>
    </div>
  );
}

/**
 * ExecutionTrendChart component
 */
export function ExecutionTrendChart({ data, periodDays, isLoading }: ExecutionTrendChartProps) {
  const successFill = tokenColor('--color-success', 'oklch(62.7% 0.194 149.2)');
  const destructiveFill = tokenColor('--color-destructive', 'oklch(62.8% 0.257 29.2)');

  // Format data for recharts
  const chartData = useMemo(() => {
    return data.map((item) => ({
      date: item.date,
      dateLabel: formatDate(item.date),
      successful: item.successful,
      failed: item.failed,
    }));
  }, [data]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Execution Trend</CardTitle>
          <CardDescription>Last {periodDays} days</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <div className="animate-pulse space-y-3 w-full">
              <div className="h-4 bg-muted rounded w-1/4" />
              <div className="h-64 bg-muted rounded" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Execution Trend</CardTitle>
          <CardDescription>Last {periodDays} days</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <p className="text-sm">No execution data available</p>
              <p className="text-xs mt-1">Run some connectors to see trends</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Execution Trend</CardTitle>
        <CardDescription>
          Successful vs Failed executions over the last {periodDays} days
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="dateLabel"
              tick={{ fontSize: 12 }}
              className="text-muted-foreground"
            />
            <YAxis
              tick={{ fontSize: 12 }}
              className="text-muted-foreground"
              allowDecimals={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: 12 }}
              iconType="square"
            />
            <Bar
              dataKey="successful"
              name="Successful"
              fill={successFill}
              radius={[4, 4, 0, 0]}
            />
            <Bar
              dataKey="failed"
              name="Failed"
              fill={destructiveFill}
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
